/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  SigninResponse,
  AuthState,
  SignupRequest,
  Tokens,
  User,
  SigninRequest,
  UpdateUserRequest,
  UpdateAvatarRequest,
} from 'store/types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { authApi, deleteToken } from 'api/Api';
import { CustomError } from 'store/types';
import { ACCESS_TIME, TokensManager } from 'utils/utils';
import { getUsers } from 'store/admin/adminSlice';

const clearTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('authTime');
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('authTime');
};

export const refreshToken = createAsyncThunk<
  Tokens,
  null,
  { rejectValue: boolean }
>('auth/refreshToken', async (args, { rejectWithValue }) => {
  try {
    const response = await authApi.refreshToken();

    return response;
  } catch (err) {
    return rejectWithValue(false);
  }
});

export const getUser = createAsyncThunk<User, null, { rejectValue: boolean }>(
  'auth/getUser',
  async (args, { rejectWithValue }) => {
    try {
      const response = await authApi.getUser();

      return response;
    } catch (err) {
      return rejectWithValue(false);
    }
  }
);

export const signinRequest = createAsyncThunk<
  SigninResponse,
  SigninRequest,
  { rejectValue: CustomError<Boolean> }
>('auth/signinRequest', async (args, { rejectWithValue }) => {
  try {
    const response = await authApi.signin(args);

    return response;
  } catch (e) {
    return rejectWithValue({
      data: false,
      errorMessage: '',
    });
  }
});

export const signupRequest = createAsyncThunk<
  User,
  SignupRequest,
  { rejectValue: CustomError<Boolean> }
>('auth/signupRequest', async (args, { rejectWithValue }) => {
  try {
    const response = await authApi.signup(args);

    return response;
  } catch (err: any) {
    return rejectWithValue({
      status: err?.response?.status,
    });
  }
});

export const updateSelf = createAsyncThunk<
  User,
  UpdateUserRequest,
  { rejectValue: boolean }
>('auth/updateSelf', async (args, { rejectWithValue }) => {
  try {
    const response = await authApi.updateSelf(args);

    return response;
  } catch (e) {
    return rejectWithValue(false);
  }
});

export const updateAvatar = createAsyncThunk<
  User,
  Promise<User>,
  { rejectValue: boolean }
>('auth/updateAvatar', async (arg, { rejectWithValue }) => {
  try {
    const response = await arg;

    return response;
  } catch (e) {
    return rejectWithValue(false);
  }
});

const initialState: AuthState = {
  globalLoadingCounter: 0,
  isAuth: false,
  isPending: false,
  result: null,
  rememberMe: JSON.parse(localStorage.getItem('rememberMe') || 'false'),
  user: null,
  tokens: new TokensManager(),

  // Finally, mutex
  isResultFrozen: false,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    dropResult: (state) => {
      state.result = null;
    },
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.rememberMe = action.payload;
      localStorage.setItem('rememberMe', JSON.stringify(action.payload));
    },
    logout: (state) => {
      clearTokens();

      deleteToken();

      state.user = null;
      state.isAuth = false;
      state.tokens = new TokensManager();
    },
    freezeResult: (state) => {
      state.isResultFrozen = true;
    },
    unfreezeResult: (state) => {
      state.isResultFrozen = true;
    },
  },
  extraReducers: (builder) => {
    // Аватар
    builder.addCase(updateAvatar.fulfilled, (state, { payload }) => {
      state.user = payload;
      state.isResultFrozen = false;
    });
    builder.addCase(updateAvatar.rejected, (state) => {
      state.isResultFrozen = false;
    });

    // Авторизация
    builder.addCase(signinRequest.pending, (state) => {
      state.isPending = true;
      state.result = null;
    });
    builder.addCase(signinRequest.fulfilled, (state, { payload }) => {
      state.isAuth = true;
      state.isPending = false;
      state.user = payload.user;

      if (state.rememberMe) {
        localStorage.setItem('accessToken', payload.access_token);
        localStorage.setItem('refreshToken', payload.refresh_token);
        localStorage.setItem('authTime', JSON.stringify(Date.now()));
      } else {
        sessionStorage.setItem('accessToken', payload.access_token);
        sessionStorage.setItem('refreshToken', payload.refresh_token);
        sessionStorage.setItem('authTime', JSON.stringify(Date.now()));
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('authTime');
      }

      state.tokens = new TokensManager();
    });
    builder.addCase(signinRequest.rejected, (state) => {
      state.isPending = false;
      state.result = {
        type: 'error',
        message: 'Упс, кажется что то пошло не так',
      };
    });

    builder.addCase(updateSelf.pending, (state) => {
      state.isPending = true;
      state.result = null;
    });
    builder.addCase(updateSelf.fulfilled, (state, { payload }) => {
      state.isPending = false;
      state.user = payload;
    });
    builder.addCase(updateSelf.rejected, (state) => {
      state.isPending = false;
    });

    builder.addCase(signupRequest.pending, (state) => {
      state.isPending = true;
      state.result = null;
    });
    builder.addCase(signupRequest.fulfilled, (state) => {
      state.isPending = false;
      state.result = {
        type: 'success',
        message:
          'Спасибо за регистрацию, ваша заявка будет рассмотрена в течение суток, если через 24 часа у вас не получилось залогиниться и начать пользоваться сервисом, пожалуйста, напишите в службу поддержки, указав вашу почту и имя, оставленные при регистрации: penskoita@transport.mos.ru',
        redirect: '/',
      };
    });
    builder.addCase(signupRequest.rejected, (state, { payload }) => {
      state.isPending = false;
      if (payload?.status === 400) {
        state.result = {
          type: 'error',
          message: 'Пользователь с данной почтой уже существует',
        };
      } else {
        state.result = {
          type: 'error',
          message: 'Упс, кажется что то пошло не так',
        };
      }
    });

    builder.addCase(getUser.pending, (state) => {
      state.globalLoadingCounter += 1;
    });
    builder.addCase(getUser.fulfilled, (state, { payload }) => {
      state.user = payload;
      state.isAuth = true;
      state.globalLoadingCounter -= 1;
    });
    builder.addCase(getUser.rejected, (state) => {
      state.globalLoadingCounter -= 1;
    });

    builder.addCase(refreshToken.pending, (state) => {
      state.globalLoadingCounter += 1;
    });
    builder.addCase(refreshToken.fulfilled, (state, { payload }) => {
      state.globalLoadingCounter -= 1;

      if (state.rememberMe) {
        localStorage.setItem('accessToken', payload.access_token);
        localStorage.setItem('refreshToken', payload.refresh_token);
        localStorage.setItem('authTime', JSON.stringify(Date.now()));
      } else {
        sessionStorage.setItem('accessToken', payload.access_token);
        sessionStorage.setItem('refreshToken', payload.refresh_token);
        sessionStorage.setItem('authTime', JSON.stringify(Date.now()));
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('authTime');
      }

      state.tokens = new TokensManager();
    });
    builder.addCase(refreshToken.rejected, (state) => {
      state.globalLoadingCounter -= 1;

      // если не рефрешнули, остается только разлогиниться
      clearTokens();
      deleteToken();
      state.user = null;
      state.isAuth = false;
      state.tokens = new TokensManager();
    });

    // Внешние
    builder.addCase(getUsers.rejected, (state, { payload }) => {
      if (payload?.status === 401) {
        const newTime = Date.now() - ACCESS_TIME - 1;

        if (state.rememberMe) {
          localStorage.setItem('authTime', String(newTime));
        } else {
          sessionStorage.setItem('authTime', String(newTime));
        }

        state.tokens = new TokensManager();
      }
    });
  },
});

export const {
  dropResult,
  setRememberMe,
  logout,
  freezeResult,
  unfreezeResult,
} = authSlice.actions;

export const selectIsAuth = (state: RootState) => state.auth.isAuth;
export const selectIsPending = (state: RootState) => state.auth.isPending;
export const selectUser = (state: RootState) => state.auth.user;
export const selectResult = (state: RootState) =>
  state.auth.isResultFrozen ? null : state.auth.result;
export const selectRole = (state: RootState) => state.auth.user?.role;
export const selectRememberMe = (state: RootState) => state.auth.rememberMe;
export const selectTokensManager = (state: RootState) => state.auth.tokens;

export default authSlice.reducer;
